'use client';

import { AlertCircle, AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

import { Alert, AlertDescription, AlertTitle, type AlertProps } from '../../primitives/alert';
import { cn } from '../../utils/cn';

const iconForTone: Record<NonNullable<UzAlertProps['tone']>, typeof AlertCircle> = {
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  destructive: AlertCircle,
};

export interface UzAlertProps extends Omit<AlertProps, 'variant' | 'title'> {
  tone?: 'info' | 'success' | 'warning' | 'destructive';
  title?: ReactNode;
  description?: ReactNode;
  /** Show a close (X) button. Call onClose to handle. */
  closable?: boolean;
  onClose?: () => void;
  /** Override the auto-selected icon. Pass null to hide it. */
  icon?: ReactNode | null;
}

export const UzAlert = forwardRef<HTMLDivElement, UzAlertProps>(
  (
    { tone = 'info', title, description, closable, onClose, icon, className, children, ...rest },
    ref,
  ) => {
    const Icon = iconForTone[tone];
    const resolvedIcon = icon === null ? null : (icon ?? <Icon className="block h-4 w-4 pr-4" />);

    return (
      <Alert ref={ref} variant={tone} className={cn('pr-10', className)} {...rest}>
        {resolvedIcon}
        {title && <AlertTitle>{title}</AlertTitle>}
        {description && <AlertDescription>{description}</AlertDescription>}
        {children}
        {closable && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Dismiss"
            className="text-muted-foreground hover:bg-muted absolute top-2 right-2 rounded-md p-1"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </Alert>
    );
  },
);
UzAlert.displayName = 'UzAlert';

// Re-export for consumers that want lower-level pieces
export type UzAlertContentProps = HTMLAttributes<HTMLDivElement>;
